/**
 * Full-page Certificate Preview & Issue (replaces the prior popup).
 *
 * Loads either by:
 *  - Pending cert id (/admin/certifications/preview/:certId) — for issuance
 *  - Eligibility (user+cert) for not-yet-pending eligibles
 *
 * Renders the EXACT uploaded Nexus Certified Investigator I template as a
 * full-bleed background image and overlays ONLY the dynamic text fields
 * (student name, certification name, course list, issue date, cert number).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../../config/canonical';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Award, Send, Download, Printer, ArrowLeft, ShieldOff, RotateCw, Loader2, CheckCircle2,
  Mail, Share2, Copy, FileDown, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const TOKEN_KEY = 'nexus_token';
const apiCall = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const formatCertNumber = (id) => {
  if (!id) return 'NTC-XX-XXXX';
  // "NTC-25-" + last 4 of id
  const yr = String(new Date().getFullYear()).slice(-2);
  return `NTC-${yr}-${(id.replace(/[^A-Z0-9]/gi, '').slice(-4) || 'XXXX').toUpperCase()}`;
};

// Short, human-friendly verification ID displayed on the certificate.
// Deterministic from verification_uuid + issued_at so the same certificate
// always resolves to the same short ID. Backend mirrors this in
// services/training_pdf.py + /verify endpoint.
const formatVerifyId = (cert) => {
  if (!cert?.verification_uuid) return '(generated on issuance)';
  const dt = cert.issued_at ? new Date(cert.issued_at) : new Date();
  const yy = String(dt.getFullYear() % 100).padStart(2, '0');
  const tail = cert.verification_uuid.replace(/-/g, '').slice(-4).toUpperCase();
  return `NexusTraining-${yy}-${tail}`;
};

/**
 * Auto-shrinking single-line text: measures the rendered width and steps
 * the font size DOWN in 1px increments (from `maxPx` toward `minPx`) until
 * the text fits inside its parent container without truncation. If the
 * name still overflows at `minPx`, we allow up to 2 centered lines instead
 * of clipping — the user's directive: "never cut off, never ellipsis".
 */
function AutoFitText({ text, maxPx = 30, minPx = 12, className = '', style = {}, ...rest }) {
  const containerRef = React.useRef(null);
  const measurerRef = React.useRef(null);
  const [size, setSize] = React.useState(maxPx);
  const [wrap, setWrap] = React.useState(false);

  React.useLayoutEffect(() => {
    const box = containerRef.current;
    const probe = measurerRef.current;
    if (!box || !probe) return;
    const boxW = box.clientWidth;
    if (!boxW) return;
    let fit = minPx;
    // Binary-ish narrow scan: try maxPx first, walk down until it fits.
    for (let px = maxPx; px >= minPx; px -= 1) {
      probe.style.fontSize = `${px}px`;
      if (probe.scrollWidth <= boxW) { fit = px; break; }
    }
    // If it never fit (probe width still > box width at minPx), wrap.
    probe.style.fontSize = `${fit}px`;
    setSize(fit);
    setWrap(probe.scrollWidth > boxW);
  }, [text, maxPx, minPx]);

  const merged = {
    ...style,
    fontSize: `${size}px`,
    whiteSpace: wrap ? 'normal' : 'nowrap',
    wordBreak: wrap ? 'break-word' : 'normal',
    lineHeight: wrap ? 1.05 : 1,
    textAlign: 'center',
    // NEVER truncate — no overflow:hidden, no ellipsis.
    overflow: 'visible',
  };
  return (
    <div ref={containerRef} className={className} style={merged} {...rest}>
      {/* Hidden measurer — mirrors font-family, weight, spacing, transform */}
      <span
        ref={measurerRef}
        aria-hidden="true"
        style={{
          position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap',
          fontFamily: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit',
          textTransform: 'inherit', pointerEvents: 'none', left: -99999, top: -99999,
        }}
      >{text}</span>
      {text}
    </div>
  );
}

/**
 * Live certificate preview canvas — background = uploaded template,
 * positioned overlays = dynamic fields. Aspect ratio matches the template.
 */
function CertificateCanvas({ certTitle, recipientName, courses, issueDate, certNumber }) {
  // Rendering contract (Feb 2026 — inpainted template):
  //   The template PNG's baked "JOHN DOE" and "TRC-101 - 108" placeholders
  //   were physically removed by a one-time pixel-level inpaint that
  //   preserved the paper texture (see
  //   /app/backend/tools/inpaint_cert_template.py, original backup at
  //   /app/backend/assets/cert_template_original.png).
  //   The certificate TITLE ("NEXUS CERTIFIED INVESTIGATOR I" — top and
  //   body-line) is preserved from the original template artwork per the
  //   current design decision. Only recipient name + course-id line are
  //   drawn dynamically on top.
  const ink = '#0F1B33';   // text ink color used in the original template
  return (
    <div data-testid="cert-canvas"
      style={{
        position: 'relative', width: '100%', aspectRatio: '1.5 / 1',
        backgroundImage: 'url(/training-center/cert_template.png)',
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
        overflow: 'hidden',
      }}>
      {/* Recipient name — auto-fitting single dynamic layer. Never
          truncates: shrinks font down to a readable minimum, then wraps
          to two centered lines if still too long. */}
      <AutoFitText
        text={(recipientName || 'STUDENT NAME').toUpperCase()}
        maxPx={30}
        minPx={12}
        data-testid="cert-canvas-name"
        style={{
          position: 'absolute', top: '46%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '48%',
          fontFamily: 'Georgia, "Times New Roman", serif',
          color: ink, fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      />

      {/* Course numbers — auto-fitting single dynamic layer. */}
      {(() => {
        const list = courses || [];
        let coursesLabel;
        if (list.length === 0) coursesLabel = 'TRC-101 — TRC-108';
        else if (list.length >= 6) coursesLabel = `${list[0]} — ${list[list.length - 1]}`;
        else coursesLabel = list.join(' · ');
        return (
          <AutoFitText
            text={coursesLabel}
            maxPx={26}
            minPx={10}
            data-testid="cert-canvas-courses"
            style={{
              position: 'absolute', top: '68%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '42%',
              fontFamily: 'Georgia, "Times New Roman", serif',
              color: ink, fontWeight: 700, letterSpacing: '0.06em',
            }}
          />
        );
      })()}

      {/* NOTE: The License Number is permanent artwork on the template and
          is NEVER dynamically rendered. Do not add another one here. */}

      {/* Issue date — positioned above the baked license-number area
          on the bottom-left, inside the safe-area, not overlapping the
          border, signature, seal, or the permanent license number. */}
      <div style={{
        position: 'absolute', top: '82%', left: '6%',
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: 'clamp(9px, 0.9vw, 12px)',
        color: ink, fontStyle: 'italic', fontWeight: 500,
      }} data-testid="cert-canvas-date">
        Issued: {issueDate}
      </div>
    </div>
  );
}

export default function CertificatePreviewPage() {
  const { certId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Editable dynamic fields
  const [recipientName, setRecipientName] = useState('');
  const [certTitle, setCertTitle] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Template-only preview: /admin/certifications/preview/template-CERT-NCI-1
        if (certId?.startsWith('template-')) {
          const cid = params.get('cert_id') || certId.replace('template-', '');
          const cat = await apiCall('/api/training-center/certifications').catch(() => ({ catalog: [] }));
          const tpl = (cat.catalog || []).find(c => c.id === cid);
          if (!tpl) throw new Error('Cert template not found');
          if (!alive) return;
          setCert({
            id: `TEMPLATE-${cid}`, status: 'TEMPLATE',
            cert_id: cid, cert_title: tpl.title,
            course_ids: tpl.course_ids || [], user_name: 'STUDENT NAME',
          });
          setCertTitle(tpl.title);
          setRecipientName('');
          setLoading(false);
          return;
        }
        // Load from pending or issued queue
        const all = await apiCall('/api/training-center/admin/certificates').catch(() => ({ certificates: [] }));
        const match = (all.certificates || []).find(c => c.id === certId);
        if (!match) {
          // try eligible scan
          const elig = await apiCall('/api/training-center/admin/certificates/eligible-scan').catch(() => ({ eligible: [] }));
          const userIdParam = params.get('user_id');
          const certIdParam = params.get('cert_id') || certId;
          const e = (elig.eligible || []).find(r => r.cert_id === certIdParam && (!userIdParam || r.user_id === userIdParam));
          if (e) {
            if (!alive) return;
            setCert({
              id: e.existing_id || `ELIG-${e.user_id}-${e.cert_id}`,
              user_id: e.user_id, user_name: e.user_name,
              cert_id: e.cert_id, cert_title: e.cert_title,
              status: e.existing_status || 'ELIGIBLE',
              course_ids: e.required_courses,
            });
            setRecipientName(e.user_name || '');
            setCertTitle(e.cert_title || '');
            setLoading(false);
            return;
          }
          throw new Error('Certificate not found');
        }
        if (!alive) return;
        setCert(match);
        setRecipientName(match.user_name || '');
        setCertTitle(match.cert_title || '');
        setExpiresAt(match.expires_at ? match.expires_at.slice(0, 10) : '');
      } catch (e) { if (alive) toast.error(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [certId, params]);

  const issueAction = async () => {
    if (!cert) return;
    setBusy(true);
    try {
      // If it's an eligibility hit without a record, the issue endpoint will
      // accept the (user_id, cert_id) and create+issue in one call.
      const endpoint = cert.id?.startsWith('ELIG-')
        ? '/api/training-center/admin/certificates/issue-direct'
        : `/api/training-center/admin/certificates/${cert.id}/issue`;
      const body = {
        user_name: recipientName, cert_title: certTitle,
        expires_at: expiresAt, notify_user: true,
        ...(cert.id?.startsWith('ELIG-') ? { user_id: cert.user_id, cert_id: cert.cert_id } : {}),
      };
      await apiCall(endpoint, { method: 'POST', body: JSON.stringify(body) });
      toast.success('Certificate issued and investigator notified');
      navigate('/admin/certifications');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!cert || cert.id?.startsWith('ELIG-')) {
      toast.error('Issue the certificate first to generate the PDF');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/training-center/certificates/${cert.id}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${formatCertNumber(cert.id)}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(e.message); }
  };

  const revokeCert = async () => {
    if (!cert || cert.id?.startsWith('ELIG-')) return;
    if (!window.confirm(`Revoke certificate for ${recipientName}? This cannot be undone via the UI.`)) return;
    try {
      await apiCall(`/api/training-center/admin/certificates/${cert.id}/revoke`, {
        method: 'POST', body: JSON.stringify({ reason: 'Admin action' }),
      });
      toast.success('Revoked');
      navigate('/admin/certifications');
    } catch (e) { toast.error(e.message); }
  };

  // Phase C action-parity: Email / Share / Copy Verify-ID / Open verification page
  const emailCert = async () => {
    if (!cert || cert.id?.startsWith('ELIG-') || cert.status !== 'ISSUED') {
      toast.error('Issue the certificate first to email it.');
      return;
    }
    const to = window.prompt('Recipient email address:', '');
    if (!to) return;
    setBusy(true);
    try {
      const res = await apiCall(
        `/api/training-center/admin/certificates/${cert.id}/email`,
        { method: 'POST', body: JSON.stringify({ to }) },
      );
      toast.success(`Emailed to ${res.to}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const shareCert = async () => {
    if (!cert?.verification_uuid) {
      toast.error('Verification UUID is generated on issuance. Issue first.');
      return;
    }
    const url = `${window.location.origin}/training-center/certificate/${cert.verification_uuid}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: cert.cert_title || 'Certificate', text: 'Verify this certificate', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success('Verification URL copied to clipboard');
    } catch (e) { /* user cancelled */ }
  };
  const copyVerifyId = async () => {
    if (!cert?.verification_uuid) {
      toast.error('Verification UUID is generated on issuance. Issue first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(cert.verification_uuid);
      toast.success('Verification ID copied');
    } catch (e) { toast.error('Copy failed'); }
  };
  const openVerification = () => {
    if (!cert?.verification_uuid) {
      toast.error('Verification UUID is generated on issuance. Issue first.');
      return;
    }
    window.open(`/training-center/certificate/${cert.verification_uuid}`, '_blank', 'noopener');
  };

  if (loading) return <div className="p-6 text-slate-300"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading…</div>;
  if (!cert) return <div className="p-6 text-slate-300">Certificate not found.</div>;

  const isPending = cert.status === 'PENDING_APPROVAL';
  const isIssued = cert.status === 'ISSUED';
  const isEligible = cert.status === 'ELIGIBLE';

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="cert-preview-page">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate('/admin/certifications')} variant="outline" className="border-slate-600 !text-slate-200" data-testid="cert-preview-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Award className="w-6 h-6 text-amber-300" /> Certificate Preview
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">{cert.id} · Status: <span className="text-amber-300 font-mono">{cert.status}</span></p>
          </div>
        </div>
      </div>

      {/* Two-column layout: edit form (left) + live preview (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4 bg-[#131A2A] border border-slate-700 rounded-lg p-5">
          <h3 className="text-base font-bold text-white">Dynamic Fields</h3>
          <p className="text-[11px] text-slate-400 -mt-2">Only these fields are editable. The certificate background, layout, logos, signature, and skyline come from the uploaded template and must not be altered.</p>
          <div>
            <Label className="text-slate-300">Student Name</Label>
            <Input value={recipientName} onChange={e => setRecipientName(e.target.value)} className="bg-slate-800 border-slate-600 text-white" data-testid="cert-edit-name" />
          </div>
          <div>
            <Label className="text-slate-300">Certification Name</Label>
            <Input value={certTitle} onChange={e => setCertTitle(e.target.value)} className="bg-slate-800 border-slate-600 text-white" data-testid="cert-edit-title" />
          </div>
          <div>
            <Label className="text-slate-300">Expiration Date (optional)</Label>
            <Input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} className="bg-slate-800 border-slate-600 text-white" data-testid="cert-edit-expires" />
          </div>
          <div className="bg-amber-500/10 border border-amber-500/40 rounded p-3 text-[12px] text-amber-200">
            <p><strong>Required courses:</strong> {(cert.course_ids || []).join(', ') || '—'}</p>
            <p className="mt-1"><strong>Certificate #:</strong> {formatCertNumber(cert.id)}</p>
            <p className="mt-1"><strong>Verification ID:</strong> <span data-testid="verification-id-display">{formatVerifyId(cert)}</span></p>
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-slate-950 border border-slate-700 rounded-lg p-3">
          <CertificateCanvas
            certTitle={certTitle}
            recipientName={recipientName}
            courses={cert.course_ids || []}
            issueDate={isIssued ? new Date(cert.issued_at).toLocaleDateString() : new Date().toLocaleDateString()}
            certNumber={formatCertNumber(cert.id)}
          />
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="cert-action-bar">
        <Button onClick={downloadPdf} variant="outline" className="border-slate-500 !text-slate-200" data-testid="cert-pdf-btn">
          <Download className="w-4 h-4 mr-1.5" /> Download PDF
        </Button>
        <Button onClick={downloadPdf} variant="outline" className="border-slate-500 !text-slate-200" data-testid="cert-export-btn" title="Export PDF (alias)">
          <FileDown className="w-4 h-4 mr-1.5" /> Export
        </Button>
        <Button onClick={() => window.print()} variant="outline" className="border-slate-500 !text-slate-200" data-testid="cert-print-btn">
          <Printer className="w-4 h-4 mr-1.5" /> Print
        </Button>
        <Button onClick={emailCert} disabled={busy || cert.status !== 'ISSUED'} variant="outline" className="border-emerald-500/40 !text-emerald-300 hover:bg-emerald-500/10" data-testid="cert-email-btn">
          <Mail className="w-4 h-4 mr-1.5" /> Email
        </Button>
        <Button onClick={shareCert} variant="outline" className="border-blue-500/40 !text-blue-300 hover:bg-blue-500/10" data-testid="cert-share-btn">
          <Share2 className="w-4 h-4 mr-1.5" /> Share
        </Button>
        <Button onClick={copyVerifyId} variant="outline" className="border-slate-500 !text-slate-200" data-testid="cert-copy-verify-btn" title="Copy Verification ID">
          <Copy className="w-4 h-4 mr-1.5" /> Copy Verify ID
        </Button>
        <Button onClick={openVerification} variant="outline" className="border-slate-500 !text-slate-200" data-testid="cert-open-verify-btn">
          <ExternalLink className="w-4 h-4 mr-1.5" /> Verification Page
        </Button>
        {isIssued && (
          <Button onClick={revokeCert} variant="outline" className="border-red-500/40 !text-red-300 hover:bg-red-500/10" data-testid="cert-revoke-btn">
            <ShieldOff className="w-4 h-4 mr-1.5" /> Revoke
          </Button>
        )}
        {(isPending || isEligible) && (
          <Button onClick={issueAction} disabled={busy} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="cert-issue-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Issuing…</> : <><Send className="w-4 h-4 mr-1.5" /> Issue & Notify Investigator</>}
          </Button>
        )}
        {isIssued && (
          <Button onClick={issueAction} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold" data-testid="cert-save-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Save Changes</>}
          </Button>
        )}
      </div>
    </div>
  );
}
